/**
 * Custom hook for managing message expansion state (references, context, stats)
 *
 * Extracted from ChatPanel.tsx to reduce complexity
 */

import { useState, useCallback } from 'react';

export type ExpansionType = 'refs' | 'context' | 'stats';

export interface UseMessageExpansionReturn {
  expandedReferences: Set<string>;
  expandedContext: Set<string>;
  expandedUsageStats: Set<string>;
  toggleExpanded: (type: ExpansionType, messageId: string) => void;
  isExpanded: (type: ExpansionType, messageId: string) => boolean;
}

/**
 * Hook to manage which message sections are expanded
 */
export function useMessageExpansion(): UseMessageExpansionReturn {
  const [expandedReferences, setExpandedReferences] = useState<Set<string>>(new Set());
  const [expandedContext, setExpandedContext] = useState<Set<string>>(new Set());
  const [expandedUsageStats, setExpandedUsageStats] = useState<Set<string>>(new Set());

  const toggleExpanded = useCallback((type: ExpansionType, messageId: string) => {
    const setter = type === 'refs' ? setExpandedReferences :
                   type === 'context' ? setExpandedContext :
                   setExpandedUsageStats;

    setter(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback((type: ExpansionType, messageId: string) => {
    const set = type === 'refs' ? expandedReferences :
                type === 'context' ? expandedContext :
                expandedUsageStats;
    return set.has(messageId);
  }, [expandedReferences, expandedContext, expandedUsageStats]);

  return {
    expandedReferences,
    expandedContext,
    expandedUsageStats,
    toggleExpanded,
    isExpanded
  };
}
